import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import TopBar from '../components/TopBar';
import PageTransition from '../components/ui/PageTransition';
import WizardStepper from '../components/ui/WizardStepper';
import Button from '../components/ui/Button';
import Alert from '../components/ui/Alert';
import { AnimatePresence } from 'framer-motion';
import './PersonalDataForm.css';

type PersonalData = {
  name: string;
  sex: string;
  race: string;
  social_name: string;
  marital_status: string;
  date_of_birth: string;
  residential_phone: string;
  cell_phone: string;
  workplace_phone: string;
  current_capacity: string;
};

const labelMap: Record<keyof PersonalData, string> = {
  name: 'Nome completo',
  sex: 'Sexo',
  race: 'Raça/Cor',
  social_name: 'Nome social',
  marital_status: 'Estado civil',
  date_of_birth: 'Data de nascimento',
  residential_phone: 'Telefone residencial',
  cell_phone: 'Celular',
  workplace_phone: 'Telefone do trabalho',
  current_capacity: 'Lotação atual',
};

const PersonalDataForm: React.FC = () => {
  const navigate = useNavigate();
  const { id_user, token } = useAuth();
  const [formData, setFormData] = useState<PersonalData>({
    name: '',
    sex: '',
    race: '',
    social_name: '',
    marital_status: '',
    date_of_birth: '',
    residential_phone: '',
    cell_phone: '',
    workplace_phone: '',
    current_capacity: '',
  });
  const [hasExistingData, setHasExistingData] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id_user || !token) {
      console.error('Token ou ID do usuário ausente', { id_user, token });
      navigate('/rj');
      return;
    }

    setIsLoading(true);
    api
      .get(`/personaldata/${id_user}`)
      .then((response) => {
        if (response.data.message === 'Personal data retrieved') {
          const { id_personal_data, id_user, date_of_birth, ...dadosFiltrados } = response.data.data;
          const formattedDate = date_of_birth
            ? new Date(date_of_birth).toLocaleDateString('pt-BR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
              })
            : '';
          setFormData({ ...dadosFiltrados, date_of_birth: formattedDate });
          setHasExistingData(true);
        }
      })
      .catch((err) => {
        console.error('Erro ao buscar dados:', err.response?.status, err.response?.data);
        setHasExistingData(false);
      })
      .finally(() => setIsLoading(false));
  }, [id_user, token, navigate]);

  const applyPhoneMask = (value: string): string => {
    let numbers = value.replace(/\D/g, '');
    if (numbers.length === 0) return '';
    if (numbers.length <= 2) return numbers;
    if (numbers.length <= 6) {
      return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
    } else if (numbers.length <= 10) {
      return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 6)}-${numbers.slice(6)}`;
    } else {
      return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7, 11)}`;
    }
  };

  const applyDateMask = (value: string): string => {
    value = value.replace(/\D/g, '').slice(0, 8);
    if (value.length >= 5) {
      return value.replace(/(\d{2})(\d{2})(\d{0,4})/, '$1/$2/$3');
    } else if (value.length >= 3) {
      return value.replace(/(\d{2})(\d{0,2})/, '$1/$2');
    }
    return value;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    let maskedValue = value;

    if (name === 'date_of_birth') {
      maskedValue = applyDateMask(value);
    } else if (['residential_phone', 'cell_phone', 'workplace_phone'].includes(name)) {
      maskedValue = applyPhoneMask(value);
    }

    setFormData({ ...formData, [name]: maskedValue });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id_user || !token) {
      console.error('Token ou ID do usuário ausente durante o envio', { id_user, token });
      navigate('/rj');
      return;
    }

    setError(null);
    setIsLoading(true);

    const cleanedFormData = {
      ...formData,
      residential_phone: formData.residential_phone.replace(/\D/g, ''),
      cell_phone: formData.cell_phone.replace(/\D/g, ''),
      workplace_phone: formData.workplace_phone.replace(/\D/g, ''),
      date_of_birth: formData.date_of_birth
        ? (() => {
            const dateParts = formData.date_of_birth.split('/');
            return dateParts.length === 3
              ? `${dateParts[2]}-${dateParts[1]}-${dateParts[0]}`
              : formData.date_of_birth;
          })()
        : '',
    };

    try {
      const url = hasExistingData ? `/personaldata/${id_user}` : '/personaldata';
      const method = hasExistingData ? 'put' : 'post';

      await api({
        method,
        url,
        data: cleanedFormData,
      });

      navigate('/rj/address');
    } catch (err: any) {
      console.error('Erro ao salvar dados:', err.response?.status, err.response?.data);
      setError(err.response?.data?.message || 'Erro ao salvar os dados.');
      setIsLoading(false);
    }
  };

  return (
    <>
      <TopBar />
      <PageTransition>
        <div className="personal-wrapper">
          <WizardStepper current={1} />
          <div className="personal-form-container">
            {isLoading && <div>Carregando dados...</div>}
            <AnimatePresence>
              {error && <Alert variant="error">{error}</Alert>}
            </AnimatePresence>
            {(!id_user || !token) && (
              <div className="session-expired">
                <h2>Sessão expirada</h2>
                <p>Faça login novamente.</p>
                <Button onClick={() => navigate('/rj')}>Ir para Login</Button>
              </div>
            )}
            {id_user && token && !isLoading && (
              <>
                <h2>Dados Pessoais</h2>
                <form onSubmit={handleSubmit} className="personal-form">
                  {Object.entries(formData).map(([key, value]) => {
                    const typedKey = key as keyof PersonalData;

                    let inputElement;

                    if (key === 'sex') {
                      inputElement = (
                        <select id={key} name={key} value={value} onChange={handleChange} disabled={isLoading}>
                          <option value="">Selecione</option>
                          <option value="male">Masculino</option>
                          <option value="female">Feminino</option>
                        </select>
                      );
                    } else if (key === 'race') {
                      inputElement = (
                        <select id={key} name={key} value={value} onChange={handleChange} disabled={isLoading}>
                          <option value="">Selecione</option>
                          <option value="white">Branco</option>
                          <option value="black">Preto</option>
                          <option value="brown">Pardo</option>
                          <option value="indigenous">Indígena</option>
                          <option value="yellow">Amarelo</option>
                        </select>
                      );
                    } else if (key === 'marital_status') {
                      inputElement = (
                        <select id={key} name={key} value={value} onChange={handleChange} disabled={isLoading}>
                          <option value="">Selecione</option>
                          <option value="single">Solteiro(a)</option>
                          <option value="married">Casado(a)</option>
                          <option value="divorced">Divorciado(a)</option>
                          <option value="widowed">Viúvo(a)</option>
                          <option value="stable_union">União Estável</option>
                        </select>
                      );
                    } else {
                      inputElement = (
                        <input
                          id={key}
                          type="text"
                          name={key}
                          value={value}
                          onChange={handleChange}
                          placeholder={
                            key === 'date_of_birth'
                              ? 'dd/mm/aaaa'
                              : key.includes('phone')
                              ? '(99) 99999-9999'
                              : ''
                          }
                          maxLength={key === 'date_of_birth' ? 10 : key.includes('phone') ? 15 : undefined}
                          disabled={isLoading}
                        />
                      );
                    }

                    return (
                      <div className="form-group" key={key}>
                        <label htmlFor={key}>{labelMap[typedKey]}:</label>
                        {inputElement}
                      </div>
                    );
                  })}

                  <div className="form-buttons">
                    <Button type="submit" loading={isLoading} disabled={isLoading}>
                      {isLoading ? 'Salvando…' : 'Próximo'}
                    </Button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      </PageTransition>
    </>
  );
};

export default PersonalDataForm;